import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { UserRole } from "@/lib/api/types";

export interface AdminSession {
  userId: string;
  email: string;
  /** Display name for the signed-in admin. */
  name: string;
  role: UserRole;
  photoUrl?: string | null;
}

interface AuthState {
  /** Short-lived JWT — in-memory only. */
  accessToken: string | null;
  /** Long-lived JWT — persisted so a hard reload can re-issue access. */
  refreshToken: string | null;
  session: AdminSession | null;
  /** ISO of last user activity, used by the idle-timer. */
  lastActivityAt: string | null;
  setTokens: (access: string, refresh: string) => void;
  setSession: (session: AdminSession) => void;
  recordActivity: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      session: null,
      lastActivityAt: null,
      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken, lastActivityAt: new Date().toISOString() }),
      setSession: (session) => set({ session }),
      recordActivity: () => set({ lastActivityAt: new Date().toISOString() }),
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          session: null,
          lastActivityAt: null,
        }),
    }),
    {
      name: "afya24.admin.auth",
      storage: createJSONStorage(() => localStorage),
      // Never persist the access token — it must die with the tab.
      partialize: (s) => ({
        refreshToken: s.refreshToken,
        session: s.session,
      }),
    },
  ),
);
